/**
 * Modal Manager
 *
 * Reference-counted modal state management with body class handling.
 * Automatically detects and handles Lenis smooth scroll (stop on open, start on close).
 * Supports extensible hooks for custom callbacks.
 *
 * Usage:
 *   import { openModal, closeModal } from '@utils/modalManager';
 *   openModal();  // Register modal as open, auto-stops Lenis if detected
 *   closeModal(); // Register modal as closed, auto-starts Lenis if detected
 *
 * Lenis Integration:
 *   - Auto-detects window.lenis and stops/starts automatically
 *   - Works whether Lenis loads before or after this module
 *   - Disable auto-handling: window.HS.modal.setLenisEnabled(false);
 *   - Manual control still available: window.HS.modal.onOpen(() => customLogic());
 *
 * External API:
 *   window.HS.modal.open();
 *   window.HS.modal.close();
 *   window.HS.modal.setLenisEnabled(false); // Disable auto Lenis handling
 *   window.HS.modal.onOpen(() => customCallback()); // Additional callbacks
 */
import config from '@config';

type ModalCallback = () => void;

let activeModalCount = 0;
const openCallbacks: ModalCallback[] = [];
const closeCallbacks: ModalCallback[] = [];

// Lenis integration settings
let lenisEnabled = true; // Auto-detect and handle Lenis by default
let lenisInstance: unknown = null; // Cache Lenis instance

/**
 * Detect and get Lenis instance from window
 */
function getLenis(): { stop: () => void; start: () => void } | null {
  if (!lenisEnabled) return null;

  // Check cached instance first
  if (lenisInstance) return lenisInstance as { stop: () => void; start: () => void };

  // Try to find Lenis on window
  const win = window as Window & { lenis?: { stop: () => void; start: () => void } };
  if (win.lenis && typeof win.lenis.stop === 'function' && typeof win.lenis.start === 'function') {
    lenisInstance = win.lenis;
    return win.lenis;
  }

  return null;
}

/**
 * Enable or disable automatic Lenis integration
 */
export function setLenisEnabled(enabled: boolean): void {
  lenisEnabled = enabled;
  if (!enabled) {
    lenisInstance = null; // Clear cache when disabled
  }
}

/**
 * Register a callback to run when first modal opens
 */
export function onModalOpen(callback: ModalCallback): () => void {
  if (typeof callback !== 'function') {
    console.warn('[modalManager] onModalOpen requires a function');
    return () => {};
  }

  openCallbacks.push(callback);

  // Return unregister function
  return () => {
    const index = openCallbacks.indexOf(callback);
    if (index > -1) openCallbacks.splice(index, 1);
  };
}

/**
 * Register a callback to run when last modal closes
 */
export function onModalClose(callback: ModalCallback): () => void {
  if (typeof callback !== 'function') {
    console.warn('[modalManager] onModalClose requires a function');
    return () => {};
  }

  closeCallbacks.push(callback);

  // Return unregister function
  return () => {
    const index = closeCallbacks.indexOf(callback);
    if (index > -1) closeCallbacks.splice(index, 1);
  };
}

/**
 * Register a modal as open
 */
export function openModal(): void {
  activeModalCount++;

  // Only apply effects when first modal opens
  if (activeModalCount === 1) {
    const modalOpenClass = config._global.classes.modalOpen;
    const overflowHiddenClass = config._global.classes.overflowHidden;

    document.body.classList.add(overflowHiddenClass, modalOpenClass);

    // Auto-stop Lenis if detected
    const lenis = getLenis();
    if (lenis) {
      try {
        lenis.stop();
      } catch (error) {
        console.error('[modalManager] Error stopping Lenis:', error);
      }
    }

    // Run all registered open callbacks
    openCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('[modalManager] Error in open callback:', error);
      }
    });
  }
}

/**
 * Register a modal as closed
 */
export function closeModal(): void {
  activeModalCount--;

  // Only remove effects when last modal closes
  if (activeModalCount === 0) {
    const modalOpenClass = config._global.classes.modalOpen;
    const overflowHiddenClass = config._global.classes.overflowHidden;

    document.body.classList.remove(overflowHiddenClass, modalOpenClass);

    // Auto-start Lenis if detected
    const lenis = getLenis();
    if (lenis) {
      try {
        lenis.start();
      } catch (error) {
        console.error('[modalManager] Error starting Lenis:', error);
      }
    }

    // Run all registered close callbacks
    closeCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('[modalManager] Error in close callback:', error);
      }
    });
  }

  // Safety: prevent negative count
  if (activeModalCount < 0) {
    console.warn('[modalManager] Modal count went negative, resetting to 0');
    activeModalCount = 0;
  }
}

/**
 * Get current modal count (for debugging)
 */
export function getActiveModalCount(): number {
  return activeModalCount;
}

/**
 * Check if any modals are currently open
 */
export function isModalOpen(): boolean {
  return activeModalCount > 0;
}

// Expose globally for external scripts
interface WindowWithHS extends Window {
  HS?: {
    modal?: {
      open: () => void;
      close: () => void;
      getCount: () => number;
      isOpen: () => boolean;
      onOpen: (callback: ModalCallback) => () => void;
      onClose: (callback: ModalCallback) => () => void;
      setLenisEnabled: (enabled: boolean) => void;
    };
  };
}

if (typeof window !== 'undefined') {
  const win = window as WindowWithHS;
  win.HS = win.HS || {};
  win.HS.modal = {
    open: openModal,
    close: closeModal,
    getCount: getActiveModalCount,
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
    setLenisEnabled: setLenisEnabled,
  };
}
